import { usePTPConfigContext } from 'GlobalConfig.context';
import { useMemo } from 'preact/hooks';

import { CustomText, CustomTextDefault } from 'components/CustomText';
import { Stack } from 'components/Stack';

import { ReturnShipment } from '../../../../models/ReturnShipment.model';
import { LastEvent } from '../LastEvent';
import { ShipmentLabelDisplay } from '../ShipmentLabelDisplay';
import { CTACancelReturn } from '../CTACancelReturn';
import { CTASelectDropOff } from '../CTASelectDropOff';

import {
  City,
  CTAButtonsContainer,
  CustomLocationIcon,
  CustomTextContainer,
  DetailsContainer,
  LocationIcon,
  ShipmentDetailsContainer,
  Location,
} from './ShipmentDetails.styles';

interface ShipmentDetailsProps {
  shipment: ReturnShipment;
}

export const ShipmentDetails = ({
  shipment,
}: ShipmentDetailsProps): JSX.Element => {
  const { returnTicket, latestMixedEvent } = shipment;

  const {
    return: { customText, returnFlow },
    trackingDetailsBox: { shouldHideEventLocations },
    parcelDetailsBox: { locationPinIcon },
  } = usePTPConfigContext();
  const isDropOffOptional = returnFlow?.dropOffSelection?.isOptional ?? false;
  const isAutoBookingPending =
    returnTicket.isBookingPending() && isDropOffOptional;

  const hasCustomText = useMemo(
    () => !!(customText.returnShipping?.text || customText.parcelDetails?.text),
    [customText],
  );

  const renderCTAButtons = () => {
    // success: both manual and optional flows
    if (returnTicket.isShipping() && !shipment.hasPickupEvents()) {
      return <ShipmentLabelDisplay shipmentPk={shipment.id} />;
    }

    // Failed booking (manual/optional flow) or auto-booking pending/in-flight: show unsuccessful UI.
    // Auto-booking pending shows the same UI as failed booking by design (ARTEMIS-6069).
    // NOTE: isFailedBooking() and isBookingPending() are only possible when the ticket is already Approved
    if (returnTicket.isFailedBooking() || isAutoBookingPending) {
      return (
        <Stack direction="column" gap={8}>
          <CustomText
            text={customText.bookingUnsuccessful?.text}
            defaultText={CustomTextDefault.BookingUnsuccessful}
          />
          <ShipmentLabelDisplay shipmentPk={shipment.id} interactive={false} />
        </Stack>
      );
    }

    // manual flow: user selects drop-off (+ cancel if still cancellable)
    if (returnTicket.isApproved() && !isDropOffOptional) {
      return (
        <>
          <CTASelectDropOff shipment={shipment} />
          {returnTicket.isCancellable() && (
            <CTACancelReturn shipment={shipment} />
          )}
        </>
      );
    }

    // pre-approval: allow cancel
    if (returnTicket.isCancellable()) {
      return <CTACancelReturn shipment={shipment} />;
    }

    return null;
  };

  return (
    <ShipmentDetailsContainer>
      <DetailsContainer>
        <LastEvent shipment={shipment} />
        {hasCustomText && (
          <CustomTextContainer>
            {returnTicket.isShipping() && !shipment.hasPickupEvents() ? (
              <CustomText
                text={
                  customText.returnShipping?.text ||
                  customText.parcelDetails?.text
                }
              />
            ) : (
              <CustomText text={customText.parcelDetails?.text} />
            )}
          </CustomTextContainer>
        )}

        {/* TODO: show confirm drop off location again when  */}
        {/* <ConfirmedDropOffLocation shipment={shipment} /> */}
        {!shouldHideEventLocations && latestMixedEvent.eventLocation && (
          <Location isClickable={false}>
            {!locationPinIcon ? (
              <LocationIcon aria-hidden />
            ) : (
              <CustomLocationIcon alt="" src={locationPinIcon} />
            )}
            <City>{latestMixedEvent.eventLocation}</City>
          </Location>
        )}
      </DetailsContainer>

      <CTAButtonsContainer>{renderCTAButtons()}</CTAButtonsContainer>
    </ShipmentDetailsContainer>
  );
};
